import React from "react";
import { Receipt } from "@mui/icons-material";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  headerGradient?: "purple" | "blue" | "green" | "gray";
  /** Stacking order when multiple modals are open (e.g. `z-[60]` above default `z-50`). */
  stackZClassName?: string;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  icon,
  children,
  size = "md",
  headerGradient = "gray",
  stackZClassName = "z-50",
}) => {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-2xl",
    lg: "max-w-4xl",
    xl: "max-w-6xl",
  };

  const responsiveSizeClasses = {
    sm: "mx-4 sm:mx-auto",
    md: "mx-4 sm:mx-auto",
    lg: "mx-4 sm:mx-auto",
    xl: "mx-4 sm:mx-auto",
  };

  const gradientClasses = {
    purple: "from-purple-50 to-purple-100 dark:from-purple-900/50 dark:to-purple-800/50",
    blue: "from-blue-50 to-blue-100 dark:from-blue-900/50 dark:to-blue-800/50",
    green: "from-green-50 to-green-100 dark:from-green-900/50 dark:to-green-800/50",
    gray: "from-gray-50 to-gray-100 dark:from-[#1A202C] dark:to-[#2D3748]",
  };

  return (
    <div className={`fixed inset-0 overflow-y-auto ${stackZClassName}`}>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      <div className="min-h-full flex items-center justify-center py-2 sm:py-4 px-2 sm:px-4">
        <div
          className={`bg-white dark:bg-[#2D3748] rounded-xl sm:rounded-2xl shadow-2xl border border-gray-200 dark:border-[#374151] w-full ${sizeClasses[size]} ${responsiveSizeClasses[size]} max-h-[95vh] sm:max-h-[90vh] flex flex-col relative overflow-hidden`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div
            className={`bg-gradient-to-r ${gradientClasses[headerGradient]} px-4 sm:px-6 py-3 sm:py-4 rounded-t-xl sm:rounded-t-2xl border-b border-gray-200 dark:border-[#374151] flex items-center justify-between gap-2 flex-shrink-0`}
          >
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <span className="flex-shrink-0 flex items-center [&>.MuiSvgIcon-root]:text-xl text-gray-600 dark:text-slate-400">
              {icon ?? <Receipt sx={{ fontSize: 24 }} />}
            </span>
              <span className="truncate">{title}</span>
            </h3>
            <button
              onClick={onClose}
              type="button"
              className="text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white text-lg sm:text-xl p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-[#374151] rounded-lg transition-colors duration-200 flex-shrink-0"
              aria-label="Fechar"
            >
              ✕
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0 relative text-gray-900 dark:text-slate-200">{children}</div>
        </div>
      </div>
    </div>
  );
};
